import "./transaction-monitoring-system-config-new-edit.scss";
import { useState, useEffect, FormEvent, useContext } from "react";
import { Button, Form } from "react-bootstrap";
import { BsArrowLeft } from "react-icons/bs";
import { BsSave } from "react-icons/bs";
import { useNavigate } from "react-router-dom";

import { IColumn } from "../../../shared/components/normal-table/normal-table";
import Spinner from "../../../shared/components/spinner/spinner";
import AuthContext from "../../../shared/context/auth-context";
import useHttp from "../../../shared/hooks/use-http";
import { postSingleNonTable, getAllData } from "../../../shared/utils/api";
import { RenderFields } from "../../../shared/utils/render-fields";
import { outputTypeOptions, scoreLocationOptions, aggregateStoreTypeOptions } from "../../../shared/utils/static-values";
import { useBack } from "../../../shared/utils/use-back";
import { validateLoadingStatusNew, getTheLastParam, createUUID } from "../../../shared/utils/utils";


const fields: IColumn[] = [
  {
    key: "name",
    displayName: "Name",
    dataType: {
      type: "text",
    },
  },
  {
    key: "output_type",
    displayName: "Output type",
    dataType: {
      type: "enumeration",
      values: outputTypeOptions,
    },
  },
  {
    key: "score_location",
    displayName: "Score location",
    dataType: {
      type: "enumeration",
      values: scoreLocationOptions,
    },
  },
  {
    key: "score_location_table_name",
    displayName: "Score location table name",
    required: false,
    controlled: true,
    dataType: {
      type: "text",
    }
  },
  {
    key: "source_table_name",
    displayName: "Source table name",
    required: false,
    controlled: true,
    dataType: {
      type: "text",
    }
  },
  {
    key: "aggregate_store_type",
    displayName: "Aggregate store type",
    dataType: {
      type: "enumeration",
      values: aggregateStoreTypeOptions,
    },
  },
  {
    key: "database_connection_name",
    displayName: "Database connection",
    dataType: {
      type: "reference",
      values: [],
    },
  },
];

const TMSConfigNewEdit = () => {
  const [initialFields, setInitialFields] = useState<IColumn[]>(fields);
  const [validated, setValidated] = useState(false);
  const [toggleWarning, setToggleWarning] = useState(false);
  const [data, setData] = useState({
    id: "",
    name: "",
    score_location: "",
    aggregate_store_type: "",
    output_type: "",
    database_connection_name: "",
    database_connection_id: "",
    score_location_table_name: "",
    source_table_name: ""
  });
  
  const authCtx = useContext(AuthContext);
  const { sendRequest: postSingleRequest, status: allStatusPost, error: errorPost} = useHttp(postSingleNonTable, false, authCtx.token, undefined, "analytics");
  const { sendRequest: getDatabaseConnections, status: databaseConnectionsStatus, data: databaseConnectionsData, error: databaseConnectionError} = useHttp(getAllData, false, authCtx.token, "databaseConnection");
  const { sendRequest: getTmsConfig, status: getTmsConfigStatus, data: getTmsConfigData, error: tmsConfigError } = useHttp(getAllData, false, authCtx.token, undefined, "analytics");
  const { handleBack } = useBack();
  const navigate = useNavigate();

  useEffect(() => {
    getDatabaseConnections("database_connections");

    if (getTheLastParam() !== "new") {
      getTmsConfig(`tmsconfig/${getTheLastParam()}`);
    }
  }, []);

  useEffect(() => {
    if (getTmsConfigStatus === "completed" && !tmsConfigError ) {
      setData({ ...getTmsConfigData.rows});
    }
  }, [getTmsConfigStatus]);

  useEffect(() => {
    if (databaseConnectionsStatus === "completed" && !databaseConnectionError) {
      const result = fields.map((column) => {
        if (column.key === "database_connection_name") {
          return { ...column, dataType: { ...column.dataType, values: databaseConnectionsData } };
        }
        return column;
      });
      setInitialFields(result);
    }
  }, [databaseConnectionsStatus]);

  useEffect(() => {
    if (allStatusPost === "completed" && !errorPost) {
      authCtx.setOnboardingAnalyticsToggleHandler();
      handleBack();
    }
  }, [allStatusPost]);

  const handleOnChange = (targetVal: string, inputKey: string, id:string) => {
    // if switching from table to csv clear the database connection
    if (inputKey === "database_connection_name" && id) {
      setData((prevState) => ({ ...prevState, database_connection_name: targetVal, database_connection_id: id }));
    } else {
      setData((prevState) => ({ ...prevState, [inputKey]: targetVal }));
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;

    if (!data.name || !data.output_type || !data.score_location || !data.aggregate_store_type) {
      setToggleWarning(true);
    }
    if (form.checkValidity()) {
      const defineId = data.id ? data.id : createUUID() ;
      setToggleWarning(false);

      if (data.aggregate_store_type === "csv") {
        postSingleRequest(`tmsconfig/${defineId}`, {
          name: data.name,
          score_location: data.score_location,
          aggregate_store_type: data.aggregate_store_type,
          output_type: data.output_type,
          score_location_table_name: data.score_location_table_name,
          source_table_name: data.source_table_name,
          id: defineId,
        });
      } else if (data.aggregate_store_type === "table") {
        postSingleRequest(`tmsconfig/${defineId}`, {
          id: defineId,
          name: data.name, 
          score_location: data.score_location, 
          aggregate_store_type: data.aggregate_store_type, 
          output_type: data.output_type, 
          database_connection_id: data.database_connection_id,
          score_location_table_name: data.score_location_table_name,
          source_table_name: data.source_table_name,
        });
      }
    }
    setValidated(true);
  };

  const renderFields = () => {
    let defineFields = [...initialFields];
    if (data.aggregate_store_type !== "table") {
      defineFields = defineFields.filter((field) => field.key !== "database_connection_name");
    }
    if (initialFields.length !== 0) {
      return <RenderFields mode="aside" fields={defineFields} data={data} handleOnChange={handleOnChange} />;
    }
  };

  const defineDisabled = () => {
    if (!data.name || !data.output_type || !data.score_location || !data.aggregate_store_type) {
      return true;
    } else {
      if (data.aggregate_store_type === "table" && !data.database_connection_name) {
        return true;
      } else {
        return false;
      }
    }
  };

  const renderPathButtons = () => {
    return (
      <span>
        <span onClick={() => navigate("/analytics/transaction-monitoring-system-config")} className="optionPath">TMS configs</span>
        <span className="optionPathDelimiter">-</span>
        <span className="selectedPathBtn">{getTheLastParam() === "new" ? "New TMS config" : "TMS config"}</span>
      </span>
    );
  };

  return (
    <div className="transactionMonitoringSystemConfigNew">
      <div onClick={() => handleBack()} className="listPage-nav-btn">
        <BsArrowLeft />
      </div>
      <div className="transactionMonitoringSystemConfigNew-header">
        <h1>
          {getTheLastParam() === "new" ? "New TMS config" : "TMS config"}
        </h1>
        {renderPathButtons()}
      </div>
      <div className="transactionMonitoringSystemConfigNew-form">
        {validateLoadingStatusNew([allStatusPost, databaseConnectionsStatus, getTmsConfigStatus]) && <Spinner />}
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          {renderFields()}
          {toggleWarning && <div className="transactionMonitoringSystemConfigNew-warning">The input is incorrect</div>}
          <div className="transactionMonitoringSystemConfigNew-buttonsGroup">
            <Button disabled={defineDisabled()} className="transactionMonitoringSystemConfigNew-save" type="submit" variant="primary">
              <BsSave />
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default TMSConfigNewEdit;
